import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { RefreshCw, Trash2 } from 'lucide-react';
import api from '../api/axios';
import { toast } from 'sonner';

const ClientErrorsLog = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/client-errors?limit=200');
      setErrors(res.data || []);
    } catch (e) {
      toast.error('Failed to load error log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, []);

  const clearErrors = async () => {
    if (!window.confirm('Clear the entire client error log?')) return;
    try {
      await api.delete('/admin/client-errors');
      setErrors([]);
      toast.success('Error log cleared');
    } catch (e) {
      toast.error('Failed to clear error log');
    }
  };

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-IN');
    } catch {
      return iso || '—';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold font-heading tracking-tight">
          Client Error Log
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchErrors} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button variant="outline" size="sm" onClick={clearErrors}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {errors.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No errors reported yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="py-2 px-3 font-semibold">Time</th>
                <th className="py-2 px-3 font-semibold">User</th>
                <th className="py-2 px-3 font-semibold">Message</th>
                <th className="py-2 px-3 font-semibold">URL</th>
                <th className="py-2 px-3 font-semibold">Stack</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((err) => (
                <tr key={err.error_id} className="border-b border-slate-100 align-top">
                  <td className="py-2 px-3 whitespace-nowrap text-xs text-muted-foreground">
                    {formatTime(err.created_at)}
                  </td>
                  <td className="py-2 px-3">
                    <p className="font-medium">{err.user_email || '—'}</p>
                  </td>
                  <td className="py-2 px-3 max-w-xs break-words">
                    <p className="font-medium text-destructive">{err.message}</p>
                  </td>
                  <td className="py-2 px-3 max-w-[180px] break-words text-xs text-muted-foreground">
                    {err.url || '—'}
                  </td>
                  <td className="py-2 px-3">
                    {err.stack ? (
                      <details className="max-w-md">
                        <summary className="text-xs text-accent cursor-pointer">View</summary>
                        <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-md p-2 mt-2 whitespace-pre-wrap break-words max-h-48 overflow-auto">
                          {err.stack}
                        </pre>
                      </details>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientErrorsLog;
